<template>
  <div class="bubble-wrapper">

    <!-- Loading State -->
    <div v-if="isLoading" class="box my-4 has-text-centered">
      <h4 class="title is-4">{{ bubbleTitle }} - טעינה</h4>
      <progress class="progress is-primary" max="100"></progress>
    </div>

    <!-- No Data Message -->
    <div v-else-if="!hasAnyData" class="box my-4 has-text-centered no-data-message">
      <h4 class="title is-4">{{ bubbleTitle }}</h4>
      <div class="notification is-warning is-light">
        <p class="has-text-weight-semibold">⚠️ לא נמצאו נתונים מתאימים עבור משימה זו</p>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="box my-4 bubble-container no-bottom-margin">
      <!-- Header with Timer -->
      <div class="bubble-header">
        <h4 class="bubble-header-title">{{ bubbleTitle }}</h4>

        <!-- Timer Display -->
        <div class="timer-display" :class="{'timer-red': isTimerRed, 'timer-blue': !isTimerRed}">
          <span class="timer-icon">⏱</span>
          <span class="timer-text">{{ formattedTimer }}</span>
        </div>
      </div>

      <div class="content has-text-right">
          <!-- Entities Section -->
        <div class="entities-section">

          <!-- Company Entity -->
          <div v-if="company" class="entity-row">
            <link-entity
              :payload="getCompanyPayload()"
              :additional_data="{ resource_type: 'Company' }"
            />
          </div>

          <!-- Position Section -->
          <PositionDetailsBlock
          v-if="position"
          :position="position"
          class="entity-section my-4"
           />

          <!-- Applicant Entity + Ghana Flag + Message -->
          <ApplicantDetailsBlock
            v-if="applicant"
            :applicant="applicant"
            :applicant-payload="getApplicantPayload()"
            :ghana-flag-payload="getApplicantPayloadForGhanaFlag()"
            @open-follow-up="openFollowUp"
            @open-magic-wand="openMagicWandModal"
            @applicant-answered="applicantAnswered"
          />

          <!-- Offer Details Block -->
          <OfferDetailsBlock v-if="offer" :offer="offer" :showEditButton="true" @offer-resolved="onOfferResolved" />
        </div>

        <!-- Checklist Section -->
        <div class="checklist-section">
          <h5 class="subtitle is-5">רשימת משימות חובה</h5>

          <div class="checklist-container">
            <!-- Combined rows with checkbox and task -->
            <div
              v-for="item in checklist"
              :key="item.id"
              class="checklist-row"
            >
              <!-- Checkbox (Left) -->
              <div class="checkbox-cell">
                <checkbox
                  :checked="item.checked"
                  @input="handleCheckboxChange(item.id, $event)"
                >
                </checkbox>
              </div>

              <!-- Task (Right) -->
              <div
                class="task-cell"
                :class="{ 'task-unchecked': !item.checked, 'task-checked': item.checked }"
              >
                <!-- Link for evaluation modal (right aligned for task 1) -->
                <button
                  v-if="item.hasLink"
                  @click="openEvaluationModal"
                  class="button is-text is-small evaluation-link"
                  title="פתיחת חלון הערכת המועמד"
                >
                  <img src="/scales.svg" width="20px" alt="Scales Icon" class="btn">
                </button>

                <span class="task-text">{{ item.text }}</span>
                <span class="task-number">.{{ item.id }}</span>
              </div>
            </div>
          </div>

          <!-- Progress Indicator -->
          <div class="progress-indicator">
            <progress
              class="progress"
              :class="isAllChecked ? 'is-success' : 'is-info'"
              :value="checkedCount"
              :max="checklist.length"
            ></progress>
          </div>
        </div>

      </div>
    </div>

    <!-- Next Offer Button - Always visible -->
    <div class="next-offer-section-always">
      <button
        @click="moveToNextOffer"
        class="button is-primary is-medium next-offer-btn"
        :title="!hasAnyData ? 'טען משימה חדשה' : 'טען הצעה הבאה מאותו סוג משימה'"
      >
        <span class="icon">⏭</span>
        <span>{{ hasAnyData ? 'הצעה הבאה' : 'משימה חדשה' }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import Checkbox from '@/components/Checkbox.vue'
import LinkEntity from '@/components/LinkEntity.vue'
import OfferDetailsBlock from '@/components/shared/organisms/OfferDetailsBlock.vue'
import ApplicantDetailsBlock from '@/components/shared/organisms/ApplicantDetailsBlock.vue'
import PositionDetailsBlock from '@/components/shared/organisms/PositionDetailsBlock.vue'
import entityPayloadMixin from '@/mixins/entityPayloadMixin'
import offerValidationMixin from '@/mixins/offerValidationMixin'

export default {
  name: 'BubbleComponent',
  mixins: [entityPayloadMixin, offerValidationMixin],
  components: {
    Checkbox,
    LinkEntity,
    OfferDetailsBlock,
    ApplicantDetailsBlock,
    PositionDetailsBlock
  },
  props: {
    alert: {
      type: Object,
      required: false,
      default: () => null
    },
    mondayTaskData: {
      type: Object,
      required: false,
      default: () => null
    }
  },
  data () {
    return {
      isLoading: false,
      isFinishing: false,
      // Timer for each offer
      timerSeconds: 0,
      timerInterval: null,
      checklist: [
        {
          id: 1,
          text: 'בחנתי את האופר של המועמד',
          hasLink: true,
          checked: false
        },
        {
          id: 2,
          text: 'בחנתי אופרים נוספים שרלוונטיים למועמד',
          checked: false
        },
        {
          id: 3,
          text: 'יש אישור הגשה חופשית + שיחה מהשבוע האחרון - הגשתי בהתאם לשק"ד',
          checked: false
        },
        {
          id: 4,
          text: 'שוחחתי טלפונית עם מועמד',
          checked: false
        },
        {
          id: 5,
          text: 'פולאפ תהליכים ומשרות מול המועמד',
          checked: false
        },
        {
          id: 6,
          text: 'סיימתי הגשות חדשות והקפצות של המועמד',
          checked: false
        }
      ]
    }
  },
  watch: {
    isAllChecked: {
      handler (isComplete) {
        // Emit completion status to parent (FeedAlert)
        this.$emit('checklist-status-changed', isComplete)
      },
      immediate: true
    },
    alert: {
      handler (newAlert, oldAlert) {
        // Reset timer when new alert arrives
        if (newAlert && (!oldAlert || newAlert.id !== oldAlert.id)) {
          this.resetTimer()
        }
      },
      deep: true
    }
  },
  computed: {
    bubbleTitle () {
      // Determine bubble type from alert designator or task name
      if (!this.alert) {
        // Loading / empty state: no alert yet, fall back to the Monday task name.
        const taskName = (this.mondayTaskData && this.mondayTaskData.task_name) || ''
        const taskTitles = {
          'בועה כחולה': '🔵 בועה כחולה',
          'בועה צהובה': '🟡 בועה צהובה',
          'בועה סגולה': '🟣 בועה סגולה',
          'בועה סטרונג מערכת': '🤖 בועה סטרונג מערכת',
          'בועה כתומה': '🟠 בועה כתומה',
          'פיד הגשות רנדומלי': '🎲 פיד הגשות רנדומלי'
        }
        return taskTitles[taskName] || 'Bubble'
      }

      const designator = this.alert.designator || ''
      const title = this.alert.title || ''
      const lowerDesignator = designator.toLowerCase()
      const lowerTitle = title.toLowerCase()

      // Check by designator (handle both singular and plural forms)
      if (lowerDesignator === 'vetted_offers' || lowerDesignator === 'vetted_offer') return '🔵 בועה כחולה'
      if (lowerDesignator === 'new_registration') return '🟡 בועה צהובה'
      if (lowerDesignator === 'system_strong_offer') return '🤖 בועה סטרונג מערכת'
      if (lowerDesignator === 'intake_review_offer') return '🟠 בועה כתומה'
      if (lowerDesignator === 'strong_offer' || lowerDesignator === 'strong_offers') return '🟣 בועה סגולה'

      // Check by title as fallback
      if (lowerTitle.includes('blue') || lowerTitle.includes('כחול')) return '🔵 בועה כחולה'
      if (lowerTitle.includes('yellow') || lowerTitle.includes('צהוב')) return '🟡 בועה צהובה'
      if (lowerTitle.includes('סטרונג מערכת')) return '🤖 בועה סטרונג מערכת'
      if (lowerTitle.includes('בועה כתומה')) return '🟠 בועה כתומה'
      if (lowerTitle.includes('purple') || lowerTitle.includes('סגול')) return '🟣 בועה סגולה'

      return 'Bubble'
    },
    isYellowBubble () {
      if (!this.alert) return false
      const designator = this.alert.designator || ''
      const lowerDesignator = designator.toLowerCase()
      return lowerDesignator === 'new_registration' || lowerDesignator.includes('yellow')
    },
    offer () {
      const baseOffer = this.alert && this.alert.resources && this.alert.resources.offer
      if (!baseOffer) return null

      return {
        ...baseOffer,
        applicant: this.applicant || baseOffer.applicant || {},
        position: this.position ? {
          ...this.position,
          company: this.company || this.position.company || {}
        } : (baseOffer.position || {})
      }
    },
    applicant () {
      return this.alert && this.alert.resources && this.alert.resources.applicant
    },
    position () {
      return this.alert && this.alert.resources && this.alert.resources.position
    },
    company () {
      return this.alert && this.alert.resources && this.alert.resources.company
    },
    operator () {
      return this.alert && this.alert.resources && this.alert.resources.operator
    },
    checkedCount () {
      return this.checklist.filter(item => item.checked).length
    },
    isAllChecked () {
      return this.checkedCount === this.checklist.length
    },
    hasAnyData () {
      // Check if we have any of the main entities needed for the bubble
      return !!(this.applicant || this.position || this.company)
    },
    formattedTimer () {
      const mins = Math.floor(this.timerSeconds / 60)
      const secs = this.timerSeconds % 60
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },
    isTimerRed () {
      return this.timerSeconds >= 40
    }
  },
  mounted () {
    console.log('Bubble component mounted')
    console.log('Alert:', this.alert)
    console.log('Monday task data:', this.mondayTaskData)

    // Debug: Check if entities exist
    console.log('=== ENTITIES DEBUG ===')
    console.log('Offer exists:', !!this.offer, this.offer)
    console.log('Applicant exists:', !!this.applicant, this.applicant)
    console.log('Position exists:', !!this.position, this.position)
    console.log('Company exists:', !!this.company, this.company)
    console.log('======================')

    if (this.alert && this.alert.resources) {
      console.log('Alert resources:', this.alert.resources)
    }

    // Start timer for this offer
    this.startTimer()
  },
  beforeUnmount () {
    this.stopTimer()
  },
  methods: {
    // R1.1 - the computed `offer` is a copy; patch the alert's own offer so the
    // bubble shows the status the server moved it to.
    onOfferResolved (event) {
      const base = this.alert && this.alert.resources && this.alert.resources.offer
      if (!base || !event || !event.status) return
      const status = base.status && typeof base.status === 'object' ? base.status : {}
      this.$set(base, 'status', { ...status, name: event.status })
    },
    handleCheckboxChange (itemId, isChecked) {
      const item = this.checklist.find(i => i.id === itemId)
      if (item) {
        item.checked = isChecked
      }
    },

    openEvaluationModal () {
      if (!this.applicant || !this.position) {
        this.$utils.emit('show_toast', {
          text: 'חסרים נתוני מועמד/משרה',
          type: 'warning'
        })
        return
      }

      this.$store.commit('modals/create', {
        designator: 'evaluate-modal',
        component_name: 'ModalEvaluate',
        modal_width: 1500,
        is_center_modal: true,
        props: {
          applicant: this.applicant,
          position: this.position
        },
        modal_title: `${this.applicant.full_name || 'מועמד'} - ${this.position.title || 'משרה'}`
      })
    },

    async finishTask () {
      if (!this.isAllChecked) {
        this.$utils.emit('show_toast', {
          text: 'יש למלא את כל המשימות לפני הסיום',
          type: 'warning'
        })
        return
      }

      this.isFinishing = true

      try {
        this.$utils.emit('show_toast', {
          text: 'המשימה הושלמה בהצלחה!',
          type: 'success'
        })

        // Emit event to parent (FeedAlert)
        this.$emit('task-completed')
      } catch (error) {
        console.error('Error finishing task:', error)
        this.$utils.emit('show_toast', {
          text: 'שגיאה בסיום המשימה',
          type: 'error'
        })
      } finally {
        this.isFinishing = false
      }
    },

    applicantAnswered () {
      if (!this.applicant) {
        this.$utils.emit('show_toast', {
          text: 'חסרים נתוני מועמד',
          type: 'warning'
        })
        return
      }

      this.$network_utils.postStat({
        title: 'Applicant answered phone',
        designator: 'applicant_answered_phone',
        status: 'active',
        owner: this.$utils.getOwner().name,
        priority: 5,
        resource_type: 'Applicant',
        resource_id: this.applicant.resource_id || this.applicant.id
      }).then(() => {
        this.$utils.emit('show_toast', {
          text: 'המועמד ענה לטלפון - נרשם בהצלחה',
          type: 'success'
        })
      }).catch((error) => {
        console.error('Error in applicantAnswered:', error)
        this.$utils.emit('show_toast', {
          text: 'שגיאה בשמירת המידע',
          type: 'error'
        })
      })
    },

    openMagicWandModal () {
      if (!this.applicant || !this.position) {
        this.$utils.emit('show_toast', {
          text: 'חסרים נתוני מועמד/משרה',
          type: 'warning'
        })
        return
      }

      // Open RelatedPositions modal (same as in offers page)
      const companyName = this.company && this.company.name ? this.company.name : ''
      this.$store.commit('modals/create', {
        designator: 'related-positions-modal',
        component_name: 'RelatedPositions',
        position: this.position,
        applicant: this.applicant,
        modal_title: `${this.position.title || 'משרה'} - ${companyName} - Related Positions`
      })
    },

    openFollowUp () {
      if (!this.applicant) {
        this.$utils.emit('show_toast', {
          text: 'חסרים נתוני מועמד',
          type: 'warning'
        })
        return
      }

      const applicantData = {
        ...this.applicant,
        resource_id: this.applicant.resource_id || this.applicant.id
      }

      this.$store.commit('modals/create', {
        designator: 'resource-alerts-form-modal',
        component_name: 'ApplicantAlertsForm',
        modal_width: 2000,
        is_center_modal: true,
        data: applicantData,
        resource_type: 'Applicant',
        modal_title: `Alerts - ${this.$utils.getResourceFullName({ resource_type: 'Applicant', data: applicantData })} ${applicantData.phone_number || ''}`
      })
    },

    /**
     * Timer Methods
     */
    startTimer () {
      this.stopTimer()
      this.timerSeconds = 0
      this.timerInterval = setInterval(() => {
        this.timerSeconds++
      }, 1000)
    },

    stopTimer () {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
    },

    resetTimer () {
      this.timerSeconds = 0
      this.startTimer()
    },

    async moveToNextOffer () {
      // If there's no data, allow moving to next task without validation
      if (!this.hasAnyData) {
        this.$emit('reload-same-task')
        return
      }

      // Validate offer before moving
      await this.validateOfferBeforeMove()
    },

    async proceedToNextOffer () {
      this.checklist.forEach(item => {
        item.checked = false
      })

      this.resetTimer()

      this.$emit('reload-same-task')
    }
  }
}
</script>

<style scoped>
/* FollowUp-inspired Container */
.bubble-container {
  background: #ffffff;
}

/* Checklist Section */
.checklist-section {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
  border: 1px solid #e0e0e0;
}

.checklist-section .subtitle {
  color: #2c3e50;
  font-weight: 700;
  margin-bottom: 0.4rem;
  font-size: 0.95rem;
}

/* Checklist Container - each row has checkbox + task */
.checklist-container {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

/* Each row contains checkbox and task side by side */
.checklist-row {
  display: flex;
  flex-direction: row-reverse;
  align-items: stretch;
  gap: 0.5rem;
}

/* Checkbox Cell (Left) - minimal width */
.checkbox-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 30px;
  padding: 0;
}

/* Smaller checkbox styling */
.checkbox-cell :deep(.checkbox) {
  display: flex !important;
  align-items: center;
  justify-content: center;
  margin: 0 !important;
  padding: 0 !important;
}

.checkbox-cell :deep(.checkmark) {
  width: 16px !important;
  height: 16px !important;
  min-width: 16px !important;
  min-height: 16px !important;
  border-width: 1.5px !important;
  margin: 0 !important;
}

.checkbox-cell :deep(.checkmark:after) {
  left: 4px !important;
  top: 1px !important;
  width: 4px !important;
  height: 8px !important;
  border-width: 0 2px 2px 0 !important;
}

/* Task Cell (Right) - takes remaining space */
.task-cell {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  background-color: #ffffff;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 0.85rem;
  line-height: 1.4;
  flex: 1;
  min-height: 2.2rem;
}

/* Red border for unchecked tasks */
.task-cell.task-unchecked {
  border: 2px solid #dc3545;
  box-shadow: 0 0 4px rgba(220, 53, 69, 0.2);
}

/* No red border for checked tasks */
.task-cell.task-checked {
  border: 1px solid #dee2e6;
}

.task-cell:hover {
  background-color: #f0f9ff;
  transform: translateX(-4px);
}

.task-cell.task-unchecked:hover {
  border-color: #dc3545;
}

.task-cell.task-checked:hover {
  border-color: #3273dc;
}

.task-number {
  font-weight: 700;
  color: #3273dc;
  margin-left: 0.3rem;
  flex-shrink: 0;
}

.task-text {
  flex: 1;
  text-align: right;
  direction: rtl;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.evaluation-link {
  padding: 2px 8px;
  font-size: 0.8rem;
  color: #3273dc;
  text-decoration: none;
  flex-shrink: 0;
  white-space: nowrap;
  align-self: center;
  margin-left: auto;
}

.evaluation-link:hover {
  text-decoration: underline;
  color: #1d4ed8;
}

/* Progress Indicator */
.progress-indicator {
  background-color: #ffffff;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.progress-indicator .progress {
  height: 1.2rem;
  border-radius: 4px;
}

.progress-indicator p {
  font-weight: 600;
  color: #4a4a4a;
  font-size: 0.85rem;
}

.entities-section .subtitle {
  color: #2c3e50;
  font-weight: 700;
  text-align: center;
  border-bottom: 2px solid #e0e0e0;
}

.entity-row {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
  margin-bottom: 0.2rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.entity-row:last-child {
  margin-bottom: 0;
}

.entity-label {
  font-weight: 700;
  color: #363636;
  font-size: 0.95rem;
  flex-shrink: 0;
  display: block;
}

.entity-with-flag {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  overflow: visible;
}

.entity-main-row {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
  flex: 1;
}

/* Candidate Message */
.candidate-message {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.6rem;
  background-color: #e8f4f8;
  border-radius: 4px;
  border: 1px solid #bee5eb;
  font-size: 0.85rem;
  width: 50%;
  min-width: 0;
}

.message-label {
  font-weight: 600;
  color: #0c5460;
  font-size: 0.8rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.message-text {
  color: #1a1a1a;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
  flex: 1;
  min-width: 0;
}

/* Ensure entity content is visible and wraps properly */
.entity-row :deep(*) {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 100%;
  overflow: visible !important;
}

/* Finish Button */
.buttons.is-centered {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-top: 2rem;
}

.button.is-large {
  padding: 1.5rem 3rem;
  font-size: 1.3rem;
  font-weight: 700;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.button.is-primary:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(50, 115, 220, 0.4);
}

.button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* No Data Message */
.no-data-message {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
}

.no-data-message .notification {
  max-width: 600px;
  font-size: 1.1rem;
  text-align: center;
  border: 2px solid #ff9800;
}

/* Icons Row */
.icons-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}

/* Phone Icon Container */
.phone-icon-container {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.phone-icon-container:hover {
  transform: scale(1.1);
}

.phone-icon-container .operation_icon {
  max-width: 25px !important;
  min-width: 25px !important;
  width: 25px !important;
}

/* Magic Wand Icon Container */
.magic-wand-icon-container {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.magic-wand-icon-container:hover {
  transform: scale(1.1);
}

.magic-wand-icon-container .operation_icon {
  max-width: 25px !important;
  min-width: 25px !important;
  width: 25px !important;
}

/* Icon Size Overrides - FollowUp style */
.bubble-container :deep(.operation_icon) {
  max-width: 16px !important;
  min-width: 16px !important;
  width: 16px !important;
}

.bubble-container :deep(.is_hot) {
  max-width: 16px !important;
  min-width: 16px !important;
}

/* Hide watch icon from position entity */
.entity-row :deep(.is_watch_image) {
  display: none !important;
}

.bubble-container :deep(.is_watch_image) {
  max-width: 16px !important;
}

.bubble-container :deep(img[src="/suitcase.svg"]) {
  width: 14px !important;
}

/* Bubble Header with Timer */
.bubble-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0.5rem 0.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0;
  border-bottom: none;
}

.bubble-header > * {
  flex-shrink: 0;
}

.bubble-header-title {
  margin: 0;
  flex: 1 1 auto;
  min-width: 150px;
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* Timer Display */
.timer-display {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 700;
  transition: all 0.3s ease;
  min-width: 70px;
  justify-content: center;
}

.timer-blue {
  background: #e3f2fd;
  color: #1976d2;
  border: 2px solid #1976d2;
}

.timer-red {
  background: #ffebee;
  color: #c62828;
  border: 2px solid #c62828;
  animation: pulse-timer 1.5s infinite;
}

@keyframes pulse-timer {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.timer-icon {
  font-size: 0.9rem;
}

.timer-text {
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
}

/* Next Offer Button Section - Always Visible */
.next-offer-section-always {
  padding: 1rem;
  background: #ffffff;
  border-top: 2px solid #e0e0e0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.next-offer-btn {
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(50, 115, 220, 0.2);
}

.next-offer-btn:not(.disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(50, 115, 220, 0.4);
}

.next-offer-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.next-offer-btn .icon {
  margin-left: 0.5rem;
}

/* Remove bottom margin from bubble container when button is outside */
.bubble-container.no-bottom-margin {
  margin-bottom: 0 !important;
}

</style>
